import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Video as VideoIcon,
  Camera,
  CaretDown,
  Play,
  Pause,
  Circle,
  Calendar,
  Clock,
  Warning,
  GridFour,
  Square,
  X,
  Plus,
  Trash,
  Image,
  FilmStrip,
  ArrowsOut,
  ArrowsIn,
  DownloadSimple,
  CameraRotate,
  Record,
  WifiHigh,
  WifiSlash,
  CaretRight,
  CaretLeft,
  MapPin,
  Speedometer,
  Check,
  Eye,
  SpeakerHigh,
  SpeakerSlash,
  Lightning,
  Monitor,
} from '@phosphor-icons/react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';
import { useAuthStore } from '@/store';
import { format, subDays, addDays, startOfDay, endOfDay, eachHourOfInterval, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import FlvPlayer from '../components/FlvPlayer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Component to handle map updates programmatically
const MapUpdater = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center && center[0] && center[1]) {
      map.setView(center, zoom || 18, { animate: true });
    }
  }, [center, zoom, map]);
  return null;
};

// Custom marker for map
const vehicleIcon = L.divIcon({
  className: 'custom-marker',
  html: `<div style="width:32px;height:32px;background:#3b82f6;border:3px solid white;border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(0,0,0,0.3);">
    <svg width="16" height="16" viewBox="0 0 256 256" fill="white"><path d="M240,112H229.2L201.42,49.5A16,16,0,0,0,186.8,40H69.2a16,16,0,0,0-14.62,9.5L26.8,112H16a8,8,0,0,0,0,16h8v80a16,16,0,0,0,16,16H64a16,16,0,0,0,16-16V192h96v16a16,16,0,0,0,16,16h24a16,16,0,0,0,16-16V128h8a8,8,0,0,0,0-16Z"/></svg>
  </div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
});

const Video = () => {
  const { imei: paramImei } = useParams();
  const navigate = useNavigate();
  const { token } = useAuthStore();
  
  const [devices, setDevices] = useState([]);
  const [deviceLocations, setDeviceLocations] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(paramImei || '');
  const [liveData, setLiveData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('live');
  
  // Channel selector state (CH1=0, CH2=1, CH3=2, CH4=3)
  const [selectedChannel, setSelectedChannel] = useState(0);
  const [autoLoadTriggered, setAutoLoadTriggered] = useState(false);
  
  // Player mode: 'iframe' (Tracksolid player) or 'native' (FLV.js direct stream)
  const [playerMode, setPlayerMode] = useState('iframe');
  const [streamUrl, setStreamUrl] = useState(null);
  const [streamLoading, setStreamLoading] = useState(false);
  const [streamError, setStreamError] = useState(null);
  
  // Map ref for programmatic control
  const mapRef = useRef(null);
  
  // DVR Mode
  const [dvrMode, setDvrMode] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [dvrCameras, setDvrCameras] = useState([]); // {imei, channel, name, url}
  const [gridSize, setGridSize] = useState(2); // 2x2, 3x3, 4x4
  const [expandedCamera, setExpandedCamera] = useState(null);
  const [loadingDvr, setLoadingDvr] = useState(false);
  
  // Media/Events - Multiple device selection
  const [mediaFiles, setMediaFiles] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [mediaFilter, setMediaFilter] = useState('all');
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [selectedDevicesForEvents, setSelectedDevicesForEvents] = useState([]); // Multi-select
  
  // Capture
  const [capturing, setCapturing] = useState(false);
  const [captureType, setCaptureType] = useState('photo'); // photo, video
  const [captureSuccess, setCaptureSuccess] = useState(null);
  
  // Timeline with range
  const [timelineDate, setTimelineDate] = useState(new Date());
  const [timelineEndDate, setTimelineEndDate] = useState(new Date());
  const [timelineEvents, setTimelineEvents] = useState([]);
  const timelineRef = useRef(null);
  
  // Historical video player
  const [historyVideoUrl, setHistoryVideoUrl] = useState(null);
  const [historyVideoLoading, setHistoryVideoLoading] = useState(false);
  
  // Media viewer modal
  const [viewerMedia, setViewerMedia] = useState(null);

  // Fetch devices with online status
  useEffect(() => {
    const fetchDevices = async () => {
      try {
        const [devicesRes, locationsRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/devices`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${BACKEND_URL}/api/devices/locations/all`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        
        const deviceList = devicesRes.data || [];
        const locations = locationsRes.data || [];
        
        // Merge with online status
        const devicesWithStatus = deviceList.map(device => {
          const loc = locations.find(l => l.imei === device.imei);
          const lastUpdate = loc?.gpsTime ? new Date(loc.gpsTime) : null;
          const isOnline = lastUpdate && (Date.now() - lastUpdate.getTime()) < 5 * 60 * 1000; // 5 min
          
          return {
            ...device,
            ...loc,
            isOnline,
            lastUpdate: lastUpdate ? format(lastUpdate, 'dd/MM HH:mm', { locale: es }) : 'Sin datos'
          };
        });
        
        setDevices(devicesWithStatus);
        setDeviceLocations(locations);
        
        if (!selectedDevice && devicesWithStatus.length > 0) {
          setSelectedDevice(devicesWithStatus[0].imei);
        }
      } catch (err) {
        console.error('Error fetching devices:', err);
      }
    };
    fetchDevices();
    
    // Refresh every 30 seconds
    const interval = setInterval(fetchDevices, 30000);
    return () => clearInterval(interval);
  }, [token, selectedDevice]);

  // Get live video URL with channel support
  const getLiveVideo = async (imei = selectedDevice, channel = selectedChannel) => {
    if (!imei) return null;
    
    setLoading(true);
    setError('');
    setLiveData(null);
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/video/live/${imei}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { channel }
      });
      const data = response.data;
      
      if (data?.UrlCamera) {
        setLiveData({ ...data, channel });
        return data;
      } else {
        setError('Este dispositivo no tiene video en vivo disponible.');
        return null;
      }
    } catch (err) {
      console.error('Error getting live video:', err);
      setError(err.response?.data?.detail || 'Error al obtener el video en vivo.');
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Auto-load video when coming from Dashboard with paramImei
  useEffect(() => {
    if (paramImei && devices.length > 0 && !autoLoadTriggered) {
      const device = devices.find(d => d.imei === paramImei);
      if (device) {
        setSelectedDevice(paramImei);
        setAutoLoadTriggered(true);
        // Auto-load video after a small delay to ensure state is set
        setTimeout(() => {
          getLiveVideo(paramImei, 0);
        }, 500);
      }
    }
  }, [paramImei, devices, autoLoadTriggered]);

  // Switch channel handler
  const switchChannel = async (channel) => {
    if (channel === selectedChannel) return;
    setSelectedChannel(channel);
    if (selectedDevice) {
      if (playerMode === 'native') {
        await getDirectStream(selectedDevice, channel);
      } else {
        await getLiveVideo(selectedDevice, channel);
      }
    }
  };

  // Get direct FLV stream URL (native player mode)
  const getDirectStream = async (imei = selectedDevice, channel = selectedChannel) => {
    if (!imei) return null;
    
    setStreamLoading(true);
    setStreamError(null);
    setStreamUrl(null);
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/video/stream/${imei}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { channel, stream_type: 1 }
      });
      
      if (response.data?.url) {
        setStreamUrl(response.data.url);
        return response.data;
      } else {
        setStreamError('No se pudo obtener el stream de video.');
        return null;
      }
    } catch (err) {
      console.error('Error getting stream:', err);
      const errorMsg = err.response?.data?.detail || 'Error al obtener el stream.';
      setStreamError(errorMsg);
      return null;
    } finally {
      setStreamLoading(false);
    }
  };

  // Toggle player mode
  const togglePlayerMode = async () => {
    const newMode = playerMode === 'iframe' ? 'native' : 'iframe';
    setPlayerMode(newMode);
    
    // Load the appropriate stream
    if (newMode === 'native' && selectedDevice) {
      await getDirectStream(selectedDevice, selectedChannel);
    } else if (newMode === 'iframe' && selectedDevice) {
      await getLiveVideo(selectedDevice, selectedChannel);
    }
  };

  // Add camera to DVR grid and load video automatically
  const addDvrCamera = async (imei, channel, deviceName, isOnline) => {
    if (dvrCameras.length >= gridSize * gridSize) {
      alert(`Máximo ${gridSize * gridSize} cámaras en grid ${gridSize}x${gridSize}`);
      return;
    }
    
    // Check if already added
    if (dvrCameras.find(c => c.imei === imei && c.channel === channel)) {
      return;
    }
    
    // Add camera with loading state
    setDvrCameras(prev => [...prev, {
      imei,
      channel,
      name: `${deviceName} - CH${channel + 1}`,
      deviceName,
      url: null,
      isOnline,
      loading: true,
      paused: false,
      muted: true // Audio off by default
    }]);
    
    // Load video automatically
    try {
      const response = await axios.get(`${BACKEND_URL}/api/video/live/${imei}?channel=${channel}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data?.UrlCamera) {
        // Add parameters to try to get live-only view
        let videoUrl = response.data.UrlCamera;
        // Try adding parameters to hide timeline/controls
        if (!videoUrl.includes('?')) {
          videoUrl += '?mode=live&hideTimeline=1';
        } else {
          videoUrl += '&mode=live&hideTimeline=1';
        }
        
        setDvrCameras(prev => prev.map(c => 
          (c.imei === imei && c.channel === channel) 
            ? { ...c, url: videoUrl, loading: false }
            : c
        ));
      } else {
        setDvrCameras(prev => prev.map(c => 
          (c.imei === imei && c.channel === channel) 
            ? { ...c, loading: false, error: true }
            : c
        ));
      }
    } catch (err) {
      console.error('Error loading camera:', err);
      setDvrCameras(prev => prev.map(c => 
        (c.imei === imei && c.channel === channel) 
          ? { ...c, loading: false, error: true }
          : c
      ));
    }
  };

  // Toggle pause for a camera
  const toggleCameraPause = (imei, channel) => {
    setDvrCameras(prev => prev.map(c => 
      (c.imei === imei && c.channel === channel) 
        ? { ...c, paused: !c.paused }
        : c
    ));
  };

  // Toggle mute for a camera
  const toggleCameraMute = (imei, channel) => {
    setDvrCameras(prev => prev.map(c => 
      (c.imei === imei && c.channel === channel) 
        ? { ...c, muted: !c.muted }
        : c
    ));
  };

  // Remove camera from DVR
  const removeDvrCamera = (imei, channel) => {
    setDvrCameras(prev => prev.filter(c => !(c.imei === imei && c.channel === channel)));
  };

  // Capture photo/video - Fixed implementation
  const captureMedia = async (type) => {
    if (!selectedDevice) return;
    
    setCapturing(true);
    setCaptureType(type);
    setCaptureSuccess(null);
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/video/capture/${selectedDevice}`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            camera: type === 'photo' ? 1 : 3, // Photo: single camera, Video: both
            media_type: type === 'photo' ? 1 : 2,
            shoot_time: 5
          }
        }
      );
      
      setCaptureSuccess(type);
      setTimeout(() => setCaptureSuccess(null), 3000);
    } catch (err) {
      console.error('Error capturing:', err);
      alert(err.response?.data?.detail || 'Error al enviar comando de captura. Verifica que el dispositivo esté online.');
    } finally {
      setCapturing(false);
    }
  };

  // Fetch combined events (alarms + media) - Support multiple devices
  const fetchMediaFiles = async () => {
    const devicesToFetch = selectedDevicesForEvents.length > 0 
      ? selectedDevicesForEvents 
      : (selectedDevice ? [selectedDevice] : []);
    
    if (devicesToFetch.length === 0) {
      alert('Selecciona al menos un vehículo');
      return;
    }
    
    setMediaLoading(true);
    setMediaFiles([]);
    
    try {
      // Use the combined events endpoint (alarms + media)
      const response = await axios.post(`${BACKEND_URL}/api/events/combined`, {
        imeis: devicesToFetch,
        begin_time: `${selectedDate} 00:00:00`,
        end_time: `${selectedDate} 23:59:59`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      let allEvents = response.data || [];
      
      // Filter by type if needed
      if (mediaFilter === 'photo') {
        allEvents = allEvents.filter(e => e.type === 'media' && e.media_type === '1');
      } else if (mediaFilter === 'video') {
        allEvents = allEvents.filter(e => e.type === 'media' && e.media_type === '2');
      } else if (mediaFilter === 'alarm') {
        allEvents = allEvents.filter(e => e.type === 'alarm');
      }
      
      setMediaFiles(allEvents);
      
      // Group by hour for timeline
      const grouped = allEvents.reduce((acc, event) => {
        if (event.time) {
          const hour = new Date(event.time).getHours();
          if (!acc[hour]) acc[hour] = [];
          acc[hour].push(event);
        }
        return acc;
      }, {});
      setTimelineEvents(grouped);
    } catch (err) {
      console.error('Error fetching events:', err);
      setMediaFiles([]);
      setTimelineEvents({});
    } finally {
      setMediaLoading(false);
    }
  };
  
  // Fetch timeline events with date range (max 31 days due to API limitation)
  const fetchTimelineEvents = async () => {
    const devicesToFetch = selectedDevicesForEvents.length > 0 
      ? selectedDevicesForEvents 
      : (selectedDevice ? [selectedDevice] : []);
    
    if (devicesToFetch.length === 0) {
      alert('Selecciona al menos un vehículo');
      return;
    }
    
    // Validate date range (max 31 days)
    const daysDiff = Math.ceil((timelineEndDate - timelineDate) / (1000 * 60 * 60 * 24));
    if (daysDiff > 31) {
      alert('El rango máximo permitido es de 31 días. Por favor ajusta las fechas.');
      return;
    }
    
    setMediaLoading(true);
    setMediaFiles([]);
    
    try {
      const startStr = format(timelineDate, 'yyyy-MM-dd') + ' 00:00:00';
      const endStr = format(timelineEndDate, 'yyyy-MM-dd') + ' 23:59:59';
      
      // Use the combined events endpoint
      const response = await axios.post(`${BACKEND_URL}/api/events/combined`, {
        imeis: devicesToFetch,
        begin_time: startStr,
        end_time: endStr
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const allEvents = response.data || [];
      setMediaFiles(allEvents);
      
      // Group by hour for timeline
      const grouped = allEvents.reduce((acc, event) => {
        if (event.time) {
          const hour = new Date(event.time).getHours();
          if (!acc[hour]) acc[hour] = [];
          acc[hour].push(event);
        }
        return acc;
      }, {});
      setTimelineEvents(grouped);
    } catch (err) {
      console.error('Error fetching timeline:', err);
      setMediaFiles([]);
      setTimelineEvents({});
    } finally {
      setMediaLoading(false);
    }
  };

  // Download file - Fixed for cross-origin
  const downloadFile = async (url, filename) => {
    try {
      // Use fetch to download the file and create a blob
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Cleanup
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Download error:', err);
      // Fallback: open in new tab
      window.open(url, '_blank');
    }
  };
  
  // Load historical video URL for timeline
  const loadHistoryVideo = async () => {
    const devicesToFetch = selectedDevicesForEvents.length > 0 
      ? selectedDevicesForEvents 
      : (selectedDevice ? [selectedDevice] : []);
    
    if (devicesToFetch.length === 0) {
      alert('Selecciona al menos un vehículo');
      return;
    }
    
    setHistoryVideoLoading(true);
    setHistoryVideoUrl(null);
    
    try {
      const imei = devicesToFetch[0]; // Use first selected device
      const startStr = format(timelineDate, 'yyyy-MM-dd') + ' 00:00:00';
      const endStr = format(timelineEndDate, 'yyyy-MM-dd') + ' 23:59:59';
      
      const response = await axios.get(`${BACKEND_URL}/api/video/history-url/${imei}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          channel: 0,
          start_time: startStr,
          end_time: endStr
        }
      });
      
      if (response.data?.UrlCamera) {
        setHistoryVideoUrl(response.data.UrlCamera);
      } else {
        alert('No hay video histórico disponible para este rango de fechas');
      }
    } catch (err) {
      console.error('Error loading history video:', err);
      alert('Error al cargar video histórico');
    } finally {
      setHistoryVideoLoading(false);
    }
  };
  
  // Toggle device selection for events
  const toggleDeviceForEvents = (imei) => {
    setSelectedDevicesForEvents(prev => 
      prev.includes(imei) 
        ? prev.filter(i => i !== imei)
        : [...prev, imei]
    );
  };
  
  // Open media viewer
  const openMediaViewer = (media) => {
    setViewerMedia(media);
  };

  // Get device info
  const getDevice = (imei) => devices.find(d => d.imei === imei);
  const currentDevice = getDevice(selectedDevice);

  // Camera channels for a device (JC450 has 4 channels)
  const getCameraChannels = (device) => {
    const model = device?.deviceModel || '';
    if (model.includes('JC450') || model.includes('JC400')) {
      return [
        { channel: 0, name: 'Frontal' },
        { channel: 1, name: 'Interior' },
        { channel: 2, name: 'Lateral Izq' },
        { channel: 3, name: 'Lateral Der' }
      ];
    }
    return [
      { channel: 0, name: 'Frontal' },
      { channel: 1, name: 'Interior' }
    ];
  };

  // Grid class
  const getGridClass = () => {
    switch (gridSize) {
      case 2: return 'grid-cols-2';
      case 3: return 'grid-cols-3';
      case 4: return 'grid-cols-4';
      default: return 'grid-cols-2';
    }
  };

  // Timeline hours
  const timelineHours = eachHourOfInterval({
    start: startOfDay(timelineDate),
    end: endOfDay(timelineDate)
  });

  return (
    <div className="min-h-screen bg-gray-100" data-testid="video-page">
      {/* Header */}
      <header className="h-16 border-b border-gray-200 flex items-center justify-between px-4 bg-white/95 backdrop-blur sticky top-0 z-10">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="p-2 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="font-bold flex items-center gap-2">
              <VideoIcon size={20} className="text-primary" />
              Video
            </h1>
            <p className="text-xs text-gray-600">
              {dvrMode ? 'Vista DVR - Múltiples cámaras' : 'Transmisión y eventos'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => { setDvrMode(false); setSelectedVehicle(null); }}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
              !dvrMode ? 'bg-primary text-primary-foreground' : 'bg-gray-200 text-gray-500'
            }`}
          >
            <Square size={16} /> Individual
          </button>
          <button
            onClick={() => setDvrMode(true)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
              dvrMode ? 'bg-primary text-primary-foreground' : 'bg-gray-200 text-gray-500'
            }`}
          >
            <GridFour size={16} /> DVR
          </button>
        </div>
      </header>

      {/* DVR Mode */}
      {dvrMode ? (
        <div className="flex h-[calc(100vh-64px)]">
          {/* Sidebar */}
          <aside className="w-80 border-r border-gray-200 bg-white/95 flex flex-col">
            <div className="p-3 border-b border-gray-200">
              <h3 className="font-medium text-sm mb-2">Configuración de Grid</h3>
              <div className="flex gap-2 mb-3">
                {[2, 3, 4].map(size => (
                  <button
                    key={size}
                    onClick={() => setGridSize(size)}
                    className={`flex-1 h-8 rounded text-xs font-medium transition-colors ${
                      gridSize === size ? 'bg-primary text-primary-foreground' : 'bg-gray-200'
                    }`}
                  >
                    {size}x{size}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-600">
                {dvrCameras.length} / {gridSize * gridSize} cámaras activas
              </p>
            </div>

            {/* Vehicle List */}
            <div className="flex-1 overflow-y-auto">
              <div className="p-2">
                <p className="text-xs text-gray-500 px-2 py-1">Selecciona un vehículo:</p>
                {devices.map(device => (
                  <div key={device.imei}>
                    {/* Vehicle row */}
                    <button
                      onClick={() => setSelectedVehicle(selectedVehicle === device.imei ? null : device.imei)}
                      className={`w-full p-2 rounded-lg mb-1 flex items-center justify-between transition-colors ${
                        selectedVehicle === device.imei ? 'bg-primary/20 border border-primary/30' : 'hover:bg-gray-200'
                      }`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <div className={`w-2 h-2 rounded-full ${device.isOnline ? 'bg-secondary' : 'bg-destructive'}`} />
                        <div className="text-left min-w-0">
                          <p className="text-sm font-medium truncate">{device.deviceName || device.imei}</p>
                          <p className="text-xs text-gray-500 flex items-center gap-1">
                            {device.isOnline ? (
                              <><WifiHigh size={10} className="text-secondary" /> Online</>
                            ) : (
                              <><WifiSlash size={10} className="text-destructive" /> Offline</>
                            )}
                            <span className="mx-1">•</span>
                            {device.lastUpdate}
                          </p>
                        </div>
                      </div>
                      <CaretRight size={16} className={`transition-transform ${selectedVehicle === device.imei ? 'rotate-90' : ''}`} />
                    </button>

                    {/* Camera channels (expanded) */}
                    {selectedVehicle === device.imei && (
                      <div className="ml-4 mb-2 space-y-1 slide-in-up">
                        {getCameraChannels(device).map(cam => {
                          const isAdded = dvrCameras.find(c => c.imei === device.imei && c.channel === cam.channel);
                          return (
                            <div
                              key={cam.channel}
                              className={`p-2 rounded-lg flex items-center justify-between ${
                                isAdded ? 'bg-secondary/20 border border-secondary/30' : 'bg-gray-100'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <Camera size={14} />
                                <span className="text-sm">{cam.name}</span>
                              </div>
                              {isAdded ? (
                                <button
                                  onClick={() => removeDvrCamera(device.imei, cam.channel)}
                                  className="p-1 rounded bg-destructive/20 text-destructive hover:bg-destructive/30"
                                >
                                  <Trash size={12} />
                                </button>
                              ) : (
                                <button
                                  onClick={() => addDvrCamera(device.imei, cam.channel, device.deviceName || device.imei, device.isOnline)}
                                  className="p-1 rounded bg-primary/20 text-primary hover:bg-primary/30"
                                >
                                  <Plus size={12} />
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Clear all */}
            {dvrCameras.length > 0 && (
              <div className="p-3 border-t border-gray-200">
                <button
                  onClick={() => setDvrCameras([])}
                  className="w-full h-9 rounded-lg bg-destructive/20 text-destructive font-medium hover:bg-destructive/30 flex items-center justify-center gap-2"
                >
                  <Trash size={16} /> Limpiar Grid
                </button>
              </div>
            )}
          </aside>

          {/* DVR Grid */}
          <main className="flex-1 p-4 bg-black">
            {expandedCamera ? (
              <div className="h-full relative">
                <button
                  onClick={() => setExpandedCamera(null)}
                  className="absolute top-2 right-2 z-10 p-2 rounded-lg bg-black/50 hover:bg-black/70 text-gray-800"
                >
                  <ArrowsIn size={20} />
                </button>
                <div className="h-full rounded-lg overflow-hidden bg-black">
                  <iframe
                    src={expandedCamera.url}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="autoplay; fullscreen"
                    allowFullScreen
                  />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
                  <p className="text-gray-800 font-medium">{expandedCamera.name}</p>
                  <p className="text-gray-800/70 text-sm">{expandedCamera.speed || 0} km/h</p>
                </div>
              </div>
            ) : dvrCameras.length > 0 ? (
              <div className={`grid ${getGridClass()} gap-2 h-full`}>
                {dvrCameras.map((cam, i) => (
                  <div key={`${cam.imei}-${cam.channel}`} className="relative bg-[#0d0d1a] rounded overflow-hidden h-full min-h-[200px]">
                    {cam.loading ? (
                      // Loading state
                      <div className="w-full h-full flex items-center justify-center">
                        <Circle size={32} className="spinner text-primary" />
                      </div>
                    ) : cam.url ? (
                      // Video iframe from Tracksolid - includes live video when device is online
                      <iframe
                        src={cam.url}
                        className="w-full h-full absolute inset-0"
                        frameBorder="0"
                        allow="autoplay"
                      />
                    ) : (
                      // No video / error - show Tracksolid placeholder
                      <div className="w-full h-full flex items-center justify-center">
                        <img 
                          src="https://us.tracksolidpro.com/resource/dev/img/img_video_novideo.b44f2b5a.png" 
                          alt="Sin video"
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-full flex items-center justify-center">
                <div className="text-center text-gray-500">
                  <GridFour size={64} className="mx-auto mb-4" />
                  <p className="text-lg font-medium">Sin cámaras seleccionadas</p>
                  <p className="text-sm">Selecciona un vehículo y agrega cámaras al grid</p>
                </div>
              </div>
            )}
          </main>
        </div>
      ) : (
        // Individual Mode
        <main className="max-w-7xl mx-auto p-6 space-y-6">
          {/* Device Select */}
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-xs text-gray-600 font-medium mb-1 block">Dispositivo</label>
                <div className="relative">
                  <select
                    value={selectedDevice}
                    onChange={(e) => {
                      setSelectedDevice(e.target.value);
                      setLiveData(null);
                      setError('');
                    }}
                    className="w-full h-10 px-3 pr-8 rounded-lg bg-white border border-gray-300 text-sm text-gray-800 appearance-none"
                  >
                    {devices.map(d => (
                      <option key={d.imei} value={d.imei}>
                        {d.isOnline ? '🟢' : '🔴'} {d.deviceName || d.imei}
                      </option>
                    ))}
                  </select>
                  <CaretDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500" />
                </div>
                {currentDevice && (
                  <p className="text-xs mt-1 flex items-center gap-1">
                    {currentDevice.isOnline ? (
                      <span className="text-secondary flex items-center gap-1"><WifiHigh size={12} /> Online</span>
                    ) : (
                      <span className="text-destructive flex items-center gap-1"><WifiSlash size={12} /> Offline</span>
                    )}
                    <span className="text-gray-500">• {currentDevice.lastUpdate}</span>
                  </p>
                )}
              </div>

              <div className="md:col-span-2 flex items-end gap-2">
                <button
                  onClick={() => setActiveTab('live')}
                  className={`flex-1 h-10 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                    activeTab === 'live' ? 'bg-primary text-primary-foreground' : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  <VideoIcon size={18} /> En Vivo
                </button>
                <button
                  onClick={() => { setActiveTab('events'); fetchMediaFiles(); }}
                  className={`flex-1 h-10 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                    activeTab === 'events' ? 'bg-primary text-primary-foreground' : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  <FilmStrip size={18} /> Eventos
                </button>
                <button
                  onClick={() => { setActiveTab('timeline'); fetchMediaFiles(); }}
                  className={`flex-1 h-10 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                    activeTab === 'timeline' ? 'bg-primary text-primary-foreground' : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  <Clock size={18} /> Timeline
                </button>
              </div>
            </div>
          </div>

          {/* Live Tab */}
          {activeTab === 'live' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Video Player */}
              <div className="lg:col-span-2 space-y-4">
                {/* Player mode toggle and control buttons */}
                <div className="flex justify-between items-center flex-wrap gap-2">
                  <div className="flex gap-2">
                    <button
                      onClick={() => playerMode === 'native' ? getDirectStream(selectedDevice, selectedChannel) : getLiveVideo(selectedDevice, selectedChannel)}
                      disabled={(loading || streamLoading) || !selectedDevice}
                      className="h-11 px-6 rounded-lg bg-secondary text-secondary-foreground font-semibold hover:bg-secondary/90 disabled:opacity-50 transition-colors flex items-center gap-2"
                      data-testid="start-stream-btn"
                    >
                      {(loading || streamLoading) ? <Circle size={18} className="spinner" /> : <Play size={18} weight="fill" />}
                      {(liveData || streamUrl) ? 'Recargar' : 'Iniciar'}
                    </button>
                    
                    {/* Capture buttons */}
                    <button
                      onClick={() => captureMedia('photo')}
                      disabled={capturing || !currentDevice?.isOnline}
                      className={`h-11 px-4 rounded-lg font-medium disabled:opacity-50 flex items-center gap-2 transition-all ${
                        captureSuccess === 'photo' 
                          ? 'bg-secondary text-secondary-foreground' 
                          : 'bg-gray-200 text-gray-800 hover:bg-gray-200/80'
                      }`}
                      title="Capturar foto"
                      data-testid="capture-photo-btn"
                    >
                      {capturing && captureType === 'photo' ? (
                        <Circle size={16} className="spinner" />
                      ) : captureSuccess === 'photo' ? (
                        <Check size={16} />
                      ) : (
                        <Camera size={16} />
                      )}
                      {captureSuccess === 'photo' ? '!' : 'Foto'}
                    </button>
                    <button
                      onClick={() => captureMedia('video')}
                      disabled={capturing || !currentDevice?.isOnline}
                      className={`h-11 px-4 rounded-lg font-medium disabled:opacity-50 flex items-center gap-2 transition-all ${
                        captureSuccess === 'video' 
                          ? 'bg-secondary text-secondary-foreground' 
                          : 'bg-gray-200 text-gray-800 hover:bg-gray-200/80'
                      }`}
                      title="Grabar video (5s)"
                      data-testid="capture-video-btn"
                    >
                      {capturing && captureType === 'video' ? (
                        <Circle size={16} className="spinner" />
                      ) : captureSuccess === 'video' ? (
                        <Check size={16} />
                      ) : (
                        <Record size={16} />
                      )}
                      {captureSuccess === 'video' ? '!' : 'Video'}
                    </button>
                  </div>
                  
                  {/* Player mode toggle */}
                  <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => setPlayerMode('iframe')}
                      className={`px-3 py-1.5 rounded text-xs font-medium flex items-center gap-1.5 transition-colors ${
                        playerMode === 'iframe' 
                          ? 'bg-white shadow text-gray-800' 
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                      title="Player Tracksolid (con timeline)"
                    >
                      <Monitor size={14} /> Tracksolid
                    </button>
                    <button
                      onClick={() => setPlayerMode('native')}
                      className={`px-3 py-1.5 rounded text-xs font-medium flex items-center gap-1.5 transition-colors ${
                        playerMode === 'native' 
                          ? 'bg-white shadow text-gray-800' 
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                      title="Player Nativo (sin overlays)"
                    >
                      <Lightning size={14} /> Directo
                    </button>
                  </div>
                </div>
                
                {/* Capture success message */}
                {captureSuccess && (
                  <div className="bg-secondary/10 border border-secondary/30 rounded-lg p-3 flex items-center gap-3">
                    <Check size={18} className="text-secondary" />
                    <p className="text-sm">
                      Comando de captura enviado. {captureSuccess === 'photo' ? 'La foto' : 'El video'} aparecerá en &quot;Eventos&quot; en unos momentos.
                    </p>
                  </div>
                )}

                {error && (
                  <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 flex items-start gap-3">
                    <Warning size={20} className="text-destructive mt-0.5" />
                    <div>
                      <p className="font-medium text-destructive">Error</p>
                      <p className="text-sm text-gray-500">{error}</p>
                    </div>
                  </div>
                )}
                
                {streamError && playerMode === 'native' && (
                  <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 flex items-start gap-3">
                    <Warning size={20} className="text-destructive mt-0.5" />
                    <div>
                      <p className="font-medium text-destructive">Error Stream Directo</p>
                      <p className="text-sm text-gray-500">{streamError}</p>
                    </div>
                  </div>
                )}

                {/* Video Player with Channel Buttons Overlay */}
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                  <div className="aspect-video bg-black relative group">
                    {/* Native Player Mode (FLV.js) */}
                    {playerMode === 'native' ? (
                      streamUrl ? (
                        <>
                          <FlvPlayer
                            url={streamUrl}
                            className="absolute inset-0 w-full h-full"
                            autoPlay={true}
                            showControls={true}
                            onError={(err) => setStreamError(err)}
                            onLoading={(loading) => setStreamLoading(loading)}
                          />
                          
                          {/* Channel selector overlay - appears on hover */}
                          <div className="absolute top-12 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-20">
                            {getCameraChannels(currentDevice).map((ch) => (
                              <button
                                key={ch.channel}
                                onClick={() => switchChannel(ch.channel)}
                                disabled={streamLoading}
                                className={`px-3 py-2 rounded-lg text-xs font-bold shadow-lg transition-all ${
                                  selectedChannel === ch.channel
                                    ? 'bg-primary text-white'
                                    : 'bg-black/70 text-white hover:bg-black/90'
                                }`}
                                title={ch.name}
                              >
                                CH{ch.channel + 1}
                              </button>
                            ))}
                          </div>
                          
                          {/* Channel indicator */}
                          <div className="absolute top-12 left-3 px-2 py-1 rounded bg-black/70 text-white text-xs font-medium z-20">
                            {getCameraChannels(currentDevice).find(c => c.channel === selectedChannel)?.name || `CH${selectedChannel + 1}`}
                          </div>
                        </>
                      ) : (
                        <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500">
                          <Lightning size={64} className="mb-4" />
                          <p className="text-lg font-medium">Stream Directo</p>
                          <p className="text-sm">
                            {currentDevice?.isOnline 
                              ? 'Presiona "Iniciar" para stream sin overlays' 
                              : 'Dispositivo offline'}
                          </p>
                        </div>
                      )
                    ) : (
                      /* Iframe Mode (Tracksolid) */
                      liveData?.UrlCamera ? (
                        <>
                          <iframe
                            src={liveData.UrlCamera}
                            className="absolute inset-0 w-full h-full"
                            frameBorder="0"
                            allow="autoplay; fullscreen"
                            allowFullScreen
                          />
                          
                          {/* Channel selector overlay - appears on hover */}
                          <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                            {getCameraChannels(currentDevice).map((ch) => (
                              <button
                                key={ch.channel}
                                onClick={() => switchChannel(ch.channel)}
                                disabled={loading}
                                className={`px-3 py-2 rounded-lg text-xs font-bold shadow-lg transition-all ${
                                  selectedChannel === ch.channel
                                    ? 'bg-primary text-white'
                                    : 'bg-black/70 text-white hover:bg-black/90'
                                }`}
                                title={ch.name}
                                data-testid={`channel-btn-${ch.channel}`}
                              >
                                CH{ch.channel + 1}
                              </button>
                            ))}
                          </div>
                          
                          {/* Channel indicator badge */}
                          <div className="absolute top-3 left-3 px-2 py-1 rounded bg-black/70 text-white text-xs font-medium z-10">
                            {getCameraChannels(currentDevice).find(c => c.channel === selectedChannel)?.name || `CH${selectedChannel + 1}`}
                          </div>
                        </>
                      ) : (
                        <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500">
                          <VideoIcon size={64} className="mb-4" />
                          <p className="text-lg font-medium">Sin transmisión</p>
                          <p className="text-sm">
                            {currentDevice?.isOnline 
                              ? 'Presiona "Iniciar" para comenzar' 
                              : 'Dispositivo offline - Video histórico disponible'}
                          </p>
                          {paramImei && !autoLoadTriggered && currentDevice?.isOnline && (
                            <p className="text-xs text-primary mt-2">Cargando automáticamente...</p>
                          )}
                        </div>
                      )
                    )}
                  </div>
                  
                  {/* Video info bar */}
                  <div className="p-3 border-t border-gray-200 flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                      {(liveData || streamUrl) ? (
                        <>
                          <div className="w-3 h-3 rounded-full bg-destructive animate-pulse" />
                          <span className="font-medium">EN VIVO</span>
                          <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                            {getCameraChannels(currentDevice).find(c => c.channel === selectedChannel)?.name || `Canal ${selectedChannel + 1}`}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded ${playerMode === 'native' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                            {playerMode === 'native' ? 'Directo' : 'Tracksolid'}
                          </span>
                        </>
                      ) : (
                        <span className="text-gray-500 text-sm">Selecciona un canal y presiona Iniciar</span>
                      )}
                    </div>
                    
                    {/* Channel buttons - always visible */}
                    <div className="flex gap-1">
                      {getCameraChannels(currentDevice).map((ch) => (
                        <button
                          key={ch.channel}
                          onClick={() => switchChannel(ch.channel)}
                          disabled={loading || streamLoading}
                          className={`px-3 py-1.5 rounded text-xs font-semibold transition-all ${
                            selectedChannel === ch.channel
                              ? 'bg-primary text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          title={ch.name}
                        >
                          {ch.name}
                        </button>
                      ))}
                    </div>
                    
                    {liveData && (
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Speedometer size={14} /> {liveData.gpsSpeed || 0} km/h
                        </span>
                        <span>{liveData.gpsTime}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Map - with auto zoom to max level (18) */}
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <div className="p-3 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="font-medium text-sm flex items-center gap-2">
                    <MapPin size={16} /> Ubicación en tiempo real
                  </h3>
                  {currentDevice?.isOnline && (
                    <span className="text-xs bg-secondary/20 text-secondary px-2 py-1 rounded-full flex items-center gap-1">
                      <WifiHigh size={12} /> Online
                    </span>
                  )}
                </div>
                <div className="h-80">
                  {currentDevice?.lat && currentDevice?.lng ? (
                    <MapContainer
                      center={[parseFloat(currentDevice.lat), parseFloat(currentDevice.lng)]}
                      zoom={18}
                      className="h-full w-full"
                      zoomControl={true}
                    >
                      <MapUpdater 
                        center={[parseFloat(currentDevice.lat), parseFloat(currentDevice.lng)]}
                        zoom={18}
                      />
                      <TileLayer
                        url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                        attribution="&copy; Esri"
                      />
                      <TileLayer
                        url="https://stamen-tiles-{s}.a.ssl.fastly.net/toner-labels/{z}/{x}/{y}{r}.png"
                        opacity={0.6}
                      />
                      <Marker 
                        position={[parseFloat(currentDevice.lat), parseFloat(currentDevice.lng)]}
                        icon={vehicleIcon}
                      >
                        <Popup>
                          <div className="text-xs">
                            <strong>{currentDevice.deviceName}</strong><br />
                            {currentDevice.speed || 0} km/h<br />
                            {currentDevice.address || 'Sin dirección'}
                          </div>
                        </Popup>
                      </Marker>
                    </MapContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-gray-500">
                      <p>Sin ubicación disponible</p>
                    </div>
                  )}
                </div>
                {currentDevice && (
                  <div className="p-3 border-t border-gray-200 text-xs text-gray-600 space-y-1">
                    <p className="truncate font-medium">{currentDevice.deviceName}</p>
                    <p className="truncate">{currentDevice.address || 'Dirección no disponible'}</p>
                    <p className="flex items-center gap-2">
                      <span>{currentDevice.lat?.toFixed(6)}, {currentDevice.lng?.toFixed(6)}</span>
                      <span className="text-gray-400">•</span>
                      <span className="flex items-center gap-1">
                        <Speedometer size={12} /> {currentDevice.speed || 0} km/h
                      </span>
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Events Tab */}
          {activeTab === 'events' && (
            <div className="space-y-4">
              {/* Filters */}
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                  {/* Multi-select vehicles */}
                  <div className="lg:col-span-2">
                    <label className="text-xs text-gray-600 font-medium mb-1 block">
                      Vehículos ({selectedDevicesForEvents.length} seleccionados)
                    </label>
                    <div className="max-h-32 overflow-y-auto border border-gray-200 rounded-lg bg-gray-50">
                      {devices.map(device => (
                        <label
                          key={device.imei}
                          className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-200/30 last:border-0"
                        >
                          <input
                            type="checkbox"
                            checked={selectedDevicesForEvents.includes(device.imei)}
                            onChange={() => toggleDeviceForEvents(device.imei)}
                            className="rounded border-gray-200"
                          />
                          <span className={`w-2 h-2 rounded-full ${device.isOnline ? 'bg-secondary' : 'bg-destructive'}`} />
                          <span className="text-sm truncate flex-1">{device.deviceName || device.imei}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-xs text-gray-600 font-medium mb-1 block">Tipo</label>
                    <div className="flex gap-1">
                      {[
                        { value: 'all', label: 'Todos', icon: GridFour },
                        { value: 'alarm', label: 'Alertas', icon: Warning },
                        { value: 'photo', label: 'Fotos', icon: Image },
                        { value: 'video', label: 'Videos', icon: FilmStrip },
                      ].map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => setMediaFilter(opt.value)}
                          className={`flex-1 h-9 rounded-lg text-xs font-medium flex items-center justify-center gap-1 transition-colors ${
                            mediaFilter === opt.value ? 'bg-primary text-primary-foreground' : 'bg-gray-200'
                          }`}
                        >
                          <opt.icon size={14} /> {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-xs text-gray-600 font-medium mb-1 block">Fecha</label>
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      className="w-full h-9 px-3 rounded-lg bg-white border border-gray-300 text-sm text-gray-800"
                    />
                  </div>
                  
                  <div className="flex items-end">
                    <button
                      onClick={fetchMediaFiles}
                      disabled={mediaLoading || selectedDevicesForEvents.length === 0}
                      className="w-full h-9 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2"
                      data-testid="search-events-btn"
                    >
                      {mediaLoading ? <Circle size={16} className="spinner" /> : <Calendar size={16} />}
                      Buscar
                    </button>
                  </div>
                </div>
              </div>

              {/* Results */}
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                {mediaLoading ? (
                  <div className="flex items-center justify-center py-16">
                    <Circle size={32} className="spinner text-primary" />
                  </div>
                ) : mediaFiles.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    {mediaFiles.map((event, i) => (
                      <div
                        key={i}
                        className="group relative aspect-video bg-gray-200 rounded-lg overflow-hidden border border-gray-200 hover:border-primary transition-colors cursor-pointer"
                        onClick={() => openMediaViewer(event)}
                      >
                        {event.type === 'alarm' ? (
                          // Alarm event (no media)
                          <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-amber-500/20 to-red-500/20 p-2">
                            <Warning size={28} className="text-amber-500 mb-1" />
                            <span className="text-[10px] text-center font-medium leading-tight text-gray-800">
                              {event.alarmTypeName}
                            </span>
                          </div>
                        ) : event.thumb_URL ? (
                          <img src={event.thumb_URL} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            {event.media_type === '1' ? <Image size={24} /> : <FilmStrip size={24} />}
                          </div>
                        )}
                        
                        {/* Hover overlay for media events */}
                        {event.type === 'media' && event.file_URL && (
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                            <button
                              onClick={(e) => { e.stopPropagation(); openMediaViewer(event); }}
                              className="p-2 rounded-lg bg-white/20 hover:bg-white/30"
                              title="Ver"
                            >
                              <Eye size={20} className="text-gray-800" />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); downloadFile(event.file_URL, `${event.deviceName || 'media'}_${event.media_type === '1' ? 'foto' : 'video'}_${i}.${event.media_type === '1' ? 'jpg' : 'mp4'}`); }}
                              className="p-2 rounded-lg bg-white/20 hover:bg-white/30"
                              title="Descargar"
                            >
                              <DownloadSimple size={20} className="text-gray-800" />
                            </button>
                          </div>
                        )}
                        
                        {/* Type badge */}
                        <div className={`absolute top-1 right-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                          event.type === 'alarm' 
                            ? 'bg-amber-500/80 text-gray-800' 
                            : event.media_type === '1' 
                              ? 'bg-blue-500/80 text-gray-800' 
                              : 'bg-red-500/80 text-gray-800'
                        }`}>
                          {event.type === 'alarm' ? 'ALERTA' : event.media_type === '1' ? 'FOTO' : 'VIDEO'}
                        </div>
                        
                        {/* Device name badge */}
                        {selectedDevicesForEvents.length > 1 && event.deviceName && (
                          <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-primary/80 text-[9px] text-gray-800 truncate max-w-[80%]">
                            {event.deviceName}
                          </div>
                        )}
                        
                        {/* Bottom info */}
                        <div className="absolute bottom-0 left-0 right-0 p-1 bg-gradient-to-t from-black/80 to-transparent">
                          {event.type === 'alarm' ? (
                            <div className="flex justify-between items-center">
                              <span className="px-1.5 py-0.5 rounded bg-black/60 text-[9px] text-gray-800 truncate max-w-[60%]">
                                {event.alarmTypeName}
                              </span>
                              {event.time && (
                                <span className="px-1.5 py-0.5 rounded bg-black/60 text-[10px] text-gray-800">
                                  {format(new Date(event.time), 'HH:mm')}
                                </span>
                              )}
                            </div>
                          ) : (
                            <div className="flex justify-between">
                              <span className="px-1.5 py-0.5 rounded bg-black/60 text-[10px] text-gray-800">
                                {event.camera === '0' ? 'Frontal' : 'Interior'}
                              </span>
                              {event.time && (
                                <span className="px-1.5 py-0.5 rounded bg-black/60 text-[10px] text-gray-800">
                                  {format(new Date(event.time), 'HH:mm')}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 text-gray-500">
                    <FilmStrip size={48} className="mb-4" />
                    <p className="font-medium">Sin eventos</p>
                    <p className="text-sm">Selecciona otra fecha o dispositivo</p>
                    <p className="text-xs mt-2 text-gray-500/70">Nota: El rango máximo es 31 días</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Timeline Tab */}
          {activeTab === 'timeline' && (
            <div className="space-y-4">
              {/* Date Range Selection */}
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                  {/* Multi-select vehicles */}
                  <div>
                    <label className="text-xs text-gray-600 font-medium mb-1 block">
                      Vehículos ({selectedDevicesForEvents.length})
                    </label>
                    <div className="max-h-24 overflow-y-auto border border-gray-200 rounded-lg bg-gray-50">
                      {devices.slice(0, 10).map(device => (
                        <label
                          key={device.imei}
                          className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-100 cursor-pointer border-b border-gray-200/30 last:border-0"
                        >
                          <input
                            type="checkbox"
                            checked={selectedDevicesForEvents.includes(device.imei)}
                            onChange={() => toggleDeviceForEvents(device.imei)}
                            className="rounded border-gray-200 w-3 h-3"
                          />
                          <span className="text-xs truncate">{device.deviceName || device.imei}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  {/* Date range */}
                  <div>
                    <label className="text-xs text-gray-600 font-medium mb-1 block">Fecha inicio</label>
                    <input
                      type="date"
                      value={format(timelineDate, 'yyyy-MM-dd')}
                      onChange={(e) => setTimelineDate(new Date(e.target.value))}
                      className="w-full h-10 px-3 rounded-lg bg-white border border-gray-300 text-sm text-gray-800"
                    />
                  </div>
                  
                  <div>
                    <label className="text-xs text-gray-600 font-medium mb-1 block">Fecha fin</label>
                    <input
                      type="date"
                      value={format(timelineEndDate, 'yyyy-MM-dd')}
                      onChange={(e) => setTimelineEndDate(new Date(e.target.value))}
                      className="w-full h-10 px-3 rounded-lg bg-white border border-gray-300 text-sm text-gray-800"
                    />
                  </div>
                  
                  <div className="flex items-end">
                    <button
                      onClick={fetchTimelineEvents}
                      disabled={mediaLoading || selectedDevicesForEvents.length === 0}
                      className="w-full h-10 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2"
                      data-testid="load-timeline-btn"
                    >
                      {mediaLoading ? <Circle size={16} className="spinner" /> : <Calendar size={16} />}
                      Cargar Alertas
                    </button>
                  </div>
                  
                  <div className="flex items-end">
                    <button
                      onClick={loadHistoryVideo}
                      disabled={historyVideoLoading || selectedDevicesForEvents.length === 0}
                      className="w-full h-10 rounded-lg bg-secondary text-secondary-foreground font-medium hover:bg-secondary/90 disabled:opacity-50 flex items-center justify-center gap-2"
                      data-testid="load-history-video-btn"
                    >
                      {historyVideoLoading ? <Circle size={16} className="spinner" /> : <FilmStrip size={16} />}
                      Ver Video SD
                    </button>
                  </div>
                </div>
                
                {/* Quick date buttons */}
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => {
                      const today = new Date();
                      setTimelineDate(today);
                      setTimelineEndDate(today);
                    }}
                    className="px-3 py-1 rounded text-xs bg-gray-200 hover:bg-gray-200/80"
                  >
                    Hoy
                  </button>
                  <button
                    onClick={() => {
                      const today = new Date();
                      setTimelineDate(subDays(today, 7));
                      setTimelineEndDate(today);
                    }}
                    className="px-3 py-1 rounded text-xs bg-gray-200 hover:bg-gray-200/80"
                  >
                    Última semana
                  </button>
                  <button
                    onClick={() => {
                      const today = new Date();
                      setTimelineDate(subDays(today, 30));
                      setTimelineEndDate(today);
                    }}
                    className="px-3 py-1 rounded text-xs bg-gray-200 hover:bg-gray-200/80"
                  >
                    Último mes
                  </button>
                </div>
              </div>

              {/* Historical Video Player */}
              {historyVideoUrl && (
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                  <div className="p-3 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="font-medium flex items-center gap-2">
                      <FilmStrip size={18} className="text-secondary" />
                      Video Histórico (SD)
                    </h3>
                    <button
                      onClick={() => setHistoryVideoUrl(null)}
                      className="p-1.5 rounded-lg hover:bg-gray-200"
                    >
                      <X size={16} />
                    </button>
                  </div>
                  <div className="aspect-video bg-black">
                    <iframe
                      src={historyVideoUrl}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="autoplay; fullscreen"
                      allowFullScreen
                    />
                  </div>
                  <div className="p-3 bg-gray-50 text-xs text-gray-600">
                    Usa el timeline del reproductor para navegar por las grabaciones de la tarjeta SD
                  </div>
                </div>
              )}

              {/* Timeline visualization */}
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                <h3 className="font-medium mb-4 flex items-center gap-2">
                  <Clock size={18} /> 
                  Línea de tiempo - {format(timelineDate, 'dd MMM', { locale: es })} 
                  {format(timelineDate, 'yyyy-MM-dd') !== format(timelineEndDate, 'yyyy-MM-dd') && (
                    <> a {format(timelineEndDate, 'dd MMM yyyy', { locale: es })}</>
                  )}
                </h3>
                
                <div className="overflow-x-auto pb-4" ref={timelineRef}>
                  <div className="min-w-[1200px]">
                    {/* Hours header */}
                    <div className="flex border-b border-gray-200 mb-2">
                      {timelineHours.map((hour, i) => (
                        <div key={i} className="flex-1 text-center text-xs text-gray-500 py-2 border-r border-gray-200 last:border-0">
                          {format(hour, 'HH:mm')}
                        </div>
                      ))}
                    </div>
                    
                    {/* Timeline bar */}
                    <div className="flex h-20 bg-gray-50 rounded-lg relative">
                      {timelineHours.map((hour, i) => {
                        const hourNum = hour.getHours();
                        const events = timelineEvents[hourNum] || [];
                        
                        return (
                          <div key={i} className="flex-1 border-r border-gray-200/30 last:border-0 relative">
                            {events.length > 0 && (
                              <div className="absolute bottom-0 left-0 right-0 px-1">
                                {events.slice(0, 3).map((event, j) => (
                                  <button
                                    key={j}
                                    onClick={() => openMediaViewer(event)}
                                    className={`block w-full h-4 mb-1 rounded text-[8px] text-gray-800 text-center leading-4 truncate hover:opacity-80 ${
                                      event.type === 'alarm' 
                                        ? 'bg-amber-500' 
                                        : event.media_type === '1' 
                                          ? 'bg-blue-500' 
                                          : 'bg-red-500'
                                    }`}
                                    title={`${event.type === 'alarm' ? event.alarmTypeName : (event.media_type === '1' ? 'Foto' : 'Video')} - ${event.time ? format(new Date(event.time), 'HH:mm:ss') : ''}`}
                                  >
                                    {event.type === 'alarm' ? 'A' : event.media_type === '1' ? 'F' : 'V'}
                                  </button>
                                ))}
                                {events.length > 3 && (
                                  <div className="text-[10px] text-center text-gray-500">
                                    +{events.length - 3}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      
                      {/* Current time indicator */}
                      {format(timelineDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd') && (
                        <div 
                          className="absolute top-0 bottom-0 w-0.5 bg-destructive"
                          style={{ left: `${(new Date().getHours() / 24) * 100}%` }}
                        />
                      )}
                    </div>
                    
                    {/* Legend */}
                    <div className="flex items-center gap-4 mt-4 text-xs text-gray-600">
                      <span className="flex items-center gap-1">
                        <div className="w-3 h-3 rounded bg-amber-500" /> Alertas
                      </span>
                      <span className="flex items-center gap-1">
                        <div className="w-3 h-3 rounded bg-blue-500" /> Fotos
                      </span>
                      <span className="flex items-center gap-1">
                        <div className="w-3 h-3 rounded bg-red-500" /> Videos
                      </span>
                      <span className="flex items-center gap-1">
                        <div className="w-3 h-0.5 bg-destructive" /> Hora actual
                      </span>
                    </div>
                  </div>
                </div>
                
                {Object.keys(timelineEvents).length === 0 && !mediaLoading && (
                  <div className="text-center py-8 text-gray-500">
                    <Clock size={32} className="mx-auto mb-2" />
                    <p>Sin eventos este día</p>
                    <p className="text-xs mt-1">Selecciona un vehículo y haz clic en &quot;Cargar Alertas&quot;</p>
                  </div>
                )}
              </div>
              
              {/* Events list below timeline */}
              {mediaFiles.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <h3 className="font-medium mb-4 flex items-center gap-2">
                    <Warning size={18} className="text-amber-500" />
                    Alertas y Eventos ({mediaFiles.length})
                  </h3>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    {mediaFiles.map((event, i) => (
                      <div
                        key={i}
                        className="group relative aspect-video bg-gray-200 rounded-lg overflow-hidden border border-gray-200 hover:border-primary transition-colors cursor-pointer"
                        onClick={() => openMediaViewer(event)}
                      >
                        {event.type === 'alarm' ? (
                          <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-amber-500/20 to-red-500/20 p-2">
                            <Warning size={28} className="text-amber-500 mb-1" />
                            <span className="text-[10px] text-center font-medium leading-tight text-gray-800">
                              {event.alarmTypeName}
                            </span>
                          </div>
                        ) : event.thumb_URL ? (
                          <img src={event.thumb_URL} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            {event.media_type === '1' ? <Image size={24} /> : <FilmStrip size={24} />}
                          </div>
                        )}
                        
                        {/* Type badge */}
                        <div className={`absolute top-1 right-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                          event.type === 'alarm' 
                            ? 'bg-amber-500/80 text-gray-800' 
                            : event.media_type === '1' 
                              ? 'bg-blue-500/80 text-gray-800' 
                              : 'bg-red-500/80 text-gray-800'
                        }`}>
                          {event.type === 'alarm' ? 'ALERTA' : event.media_type === '1' ? 'FOTO' : 'VIDEO'}
                        </div>
                        
                        {/* Bottom info */}
                        <div className="absolute bottom-0 left-0 right-0 p-1 bg-gradient-to-t from-black/80 to-transparent">
                          <div className="flex justify-between items-center">
                            <span className="px-1.5 py-0.5 rounded bg-black/60 text-[9px] text-gray-800 truncate max-w-[60%]">
                              {event.alarmTypeName || (event.media_type === '1' ? 'Foto' : 'Video')}
                            </span>
                            {event.time && (
                              <span className="px-1.5 py-0.5 rounded bg-black/60 text-[10px] text-gray-800">
                                {format(new Date(event.time), 'HH:mm')}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </main>
      )}
      
      {/* Media Viewer Modal */}
      {viewerMedia && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setViewerMedia(null)}
        >
          <div 
            className="relative max-w-5xl w-full max-h-[90vh] bg-white rounded-xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <h3 className="font-medium flex items-center gap-2">
                  {viewerMedia.type === 'alarm' ? (
                    <>
                      <Warning size={18} className="text-amber-500" />
                      {viewerMedia.alarmTypeName} - {viewerMedia.deviceName || 'Dispositivo'}
                    </>
                  ) : (
                    <>
                      {viewerMedia.media_type === '1' ? 'Foto' : 'Video'} - {viewerMedia.deviceName || 'Dispositivo'}
                    </>
                  )}
                </h3>
                <p className="text-sm text-gray-500">
                  {viewerMedia.time && format(new Date(viewerMedia.time), 'dd/MM/yyyy HH:mm:ss', { locale: es })}
                  {viewerMedia.type === 'media' && (
                    <>
                      {' • '}
                      Cámara: {viewerMedia.camera === '0' ? 'Frontal' : 'Interior'}
                    </>
                  )}
                  {viewerMedia.speed && (
                    <>
                      {' • '}
                      Velocidad: {viewerMedia.speed} km/h
                    </>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {viewerMedia.file_URL && (
                  <button
                    onClick={() => downloadFile(viewerMedia.file_URL, `${viewerMedia.deviceName || 'media'}_${viewerMedia.media_type === '1' ? 'foto' : 'video'}.${viewerMedia.media_type === '1' ? 'jpg' : 'mp4'}`)}
                    className="p-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
                    title="Descargar"
                  >
                    <DownloadSimple size={20} />
                  </button>
                )}
                <button
                  onClick={() => setViewerMedia(null)}
                  className="p-2 rounded-lg bg-gray-200 hover:bg-gray-200/80"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-4">
              {viewerMedia.type === 'alarm' ? (
                // Alarm event detail
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-amber-500/20 to-red-500/20 flex items-center justify-center mb-6">
                    <Warning size={48} className="text-amber-500" />
                  </div>
                  <h2 className="text-2xl font-bold mb-2">{viewerMedia.alarmTypeName}</h2>
                  <p className="text-gray-500 mb-4">
                    {viewerMedia.time && format(new Date(viewerMedia.time), 'EEEE, dd MMMM yyyy HH:mm:ss', { locale: es })}
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4 w-full max-w-md">
                    <div className="bg-gray-100 rounded-lg p-4 text-center">
                      <p className="text-xs text-gray-500 mb-1">Dispositivo</p>
                      <p className="font-medium">{viewerMedia.deviceName}</p>
                    </div>
                    {viewerMedia.speed && (
                      <div className="bg-gray-100 rounded-lg p-4 text-center">
                        <p className="text-xs text-gray-500 mb-1">Velocidad</p>
                        <p className="font-medium">{viewerMedia.speed} km/h</p>
                      </div>
                    )}
                    {viewerMedia.lat && viewerMedia.lng && (
                      <div className="bg-gray-100 rounded-lg p-4 text-center col-span-2">
                        <p className="text-xs text-gray-500 mb-1">Ubicación</p>
                        <p className="font-medium">{viewerMedia.lat}, {viewerMedia.lng}</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : viewerMedia.media_type === '1' ? (
                // Photo
                <img 
                  src={viewerMedia.file_URL} 
                  alt="Captura" 
                  className="max-w-full max-h-[70vh] mx-auto rounded-lg"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : (
                // Video
                <video
                  src={viewerMedia.file_URL}
                  controls
                  autoPlay
                  className="max-w-full max-h-[70vh] mx-auto rounded-lg"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                >
                  Tu navegador no soporta la reproducción de video.
                </video>
              )}
              
              {/* Fallback message */}
              {viewerMedia.type !== 'alarm' && (
                <div className="hidden flex-col items-center justify-center py-16 text-gray-500">
                  <Warning size={48} className="mb-4" />
                  <p className="font-medium">No se pudo cargar el archivo</p>
                  <p className="text-sm mb-4">El archivo puede no estar disponible o haber expirado</p>
                  <a
                    href={viewerMedia.file_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 rounded-lg bg-primary text-primary-foreground"
                  >
                    Abrir en nueva pestaña
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Video;
